import {Component, Inject, OnInit, PLATFORM_ID} from '@angular/core';
import {FaIconComponent} from "@fortawesome/angular-fontawesome";
import {faLock, faSpinner} from "@fortawesome/free-solid-svg-icons";
import {
  AbstractControl,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidatorFn,
  Validators
} from "@angular/forms";
import {HeaderComponent} from "../../shared/components/header/header.component";
import {MatOption} from "@angular/material/core";
import {MatProgressSpinner} from "@angular/material/progress-spinner";
import {MatSelect} from "@angular/material/select";
import {isPlatformBrowser, Location, NgClass, NgForOf, NgIf} from "@angular/common";
import {TranslateModule} from "@ngx-translate/core";
import {CardNewModel, City, Country, State} from "../../shared/models";
import {CARD_PREDICTORS_INFO, CARD_TYPES} from "../../shared/enum/card-info.enum";
import {MatDialog} from "@angular/material/dialog";
import {AddressService} from "../../shared/services/address.service";
import {StorageService} from "../../shared/services/storage.service";
import {ActivatedRoute} from "@angular/router";
import {CaptchaModalComponent} from "../../shared/components/captcha-modal/captcha-modal.component";
import {NotificationModalComponent} from "../../shared/components/notification-modal/notification-modal.component";
import {CheckoutService} from "../../shared/services/checkout.service";
import {NotificationService} from "../../shared/services/notification.service";
import {AuthService} from "../../shared/services/auth.service";
import {CartService} from "../../shared/services/cart.service";

@Component({
  selector: 'app-guest-payment',
  standalone: true,
  imports: [
    FaIconComponent,
    FormsModule,
    HeaderComponent,
    MatOption,
    MatProgressSpinner,
    MatSelect,
    NgForOf,
    NgIf,
    ReactiveFormsModule,
    TranslateModule,
    NgClass
  ],
  templateUrl: './guest-payment.component.html'
})
export class GuestPaymentComponent implements OnInit {
  debitCardOnly = false;
  paymentCardId: string = '';
  card: CardNewModel | undefined = undefined;
  isFilterByDebits: boolean = false;
  billingCountriesToHide: string[] = [];

  token: string | null = null;
  is_human = false;
  isLoading: boolean = false;
  isSkeleton: boolean = true;
  cardLogo = '';
  largeMaxLength: number = 19;
  faSpinner = faSpinner;
  protected readonly faLock = faLock;
  wrongDate: boolean = false;
  cardPredictors = CARD_PREDICTORS_INFO;

  formMarkAsTouched: boolean = false;

  countries: Country[] = [];
  states: State[] = [];
  cities: City[] = [];
  allCountries: Country[] = [];
  postalCodes: string[] = [];
  isLoadingCountries: boolean = true;
  isLoadingAllCountries: boolean = true;
  isLoadingStates: boolean = true;
  isLoadingCities: boolean = true;
  showSelectInput: boolean = true;
  isLoadingPhones: boolean = true;
  isPayment: boolean = false;

  countryCu: Country = { id: 51, iso: 'CU', iso3: 'CUB', iso_name: 'CUBA', name: 'CUBA', numcode: 192 };
  countryIsoSelected = 'us';
  countryPrefix = '+1';

  cardForm!: FormGroup;

  paymentRoute: any;

  constructor(
    private dialog: MatDialog,
    private addressService: AddressService,
    private location: Location,
    private checkoutService: CheckoutService,
    private readonly _storageService: StorageService,
    private activatedRoute: ActivatedRoute,
    private notificationService: NotificationService,
    private authService: AuthService,
    private cartService: CartService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  ngOnInit(): void {
    const paramMap = this.activatedRoute.snapshot.queryParamMap;
    this.paymentRoute = paramMap.get('paymentRoute')
    if (this._storageService.isPlatformBrowser()){
      const state = window.history.state as {
        isModeEdit: boolean,
        data: CardNewModel,
        isFilterByDebits: boolean,
        debitCardOnly: boolean,
        billingCountriesToHide: string[],
        isRevalidate: boolean,
        isRevalidateCheckout: boolean,
        paymentCardId: string,
        isPayment: boolean
      };
      this.card                   = state.data || false;
      this.isFilterByDebits       = state.isFilterByDebits || false;
      this.debitCardOnly          = state.debitCardOnly || false;
      this.billingCountriesToHide = state.billingCountriesToHide || [];
      this.paymentCardId          = state.paymentCardId || '';
      this.isPayment              = state.isPayment || false;
      this.getAllCountries();
      this.initializeForm();
    }
  }

  initializeForm(): void {
      this.cardForm = new FormGroup({
        email: new FormControl(null, [Validators.required, Validators.email]),
        expiration: new FormControl(null, [Validators.required, Validators.pattern(/^\d{2}\/\d{2}$/)]),
        name: new FormControl(null, [Validators.required, Validators.pattern(/^(?!\s*$).+$/)]),
        lastname: new FormControl(null, [Validators.required, Validators.pattern(/^(?!\s*$).+$/)]),
        addressLine: new FormControl(null, [Validators.required]),
        number: new FormControl(null, [Validators.required, Validators.pattern(/^[0-9\s]*$/), this.customValidator().bind(this)]),
        verification_value: new FormControl(null, [Validators.required, Validators.pattern(/^[0-9]*$/), Validators.minLength(3), Validators.maxLength(4)]),
        cc_type: new FormControl(null, Validators.required),
        city: new FormControl(null, Validators.required),
        state_name: new FormControl(null, Validators.required),
        country_iso: new FormControl(null, Validators.required),
        zipcode: new FormControl(null, [Validators.required, Validators.pattern(/^[a-zA-Z0-9-]+$/)]),
      });
  }

  onCancel(): void {
    this.location.back();
  }

  async onSubmit() {
    this.isLoading = true;
    this.cardForm.markAllAsTouched();
    this.formMarkAsTouched = true;
    if (!this.cardForm.valid) {
      this.isLoading = false;
    }else {
      const paramMap = this.activatedRoute.snapshot.queryParamMap;
      const dialogRef = this.dialog.open(CaptchaModalComponent);
      const token = await dialogRef.afterClosed().toPromise();
      if (token) {
        const expiration = this.cardForm.get('expiration')?.value.split('/');
        const paymentIntentionData : any = {
          totalAmount: paramMap.get('total') ?? undefined,
          cardNumber: this.cardForm.get('number')?.value.replace(/\s/g, ''),
          expMonth: expiration[0],
          expYear: expiration[1],
          cvv: this.cardForm.get('verification_value')?.value,
          firstName: this.cardForm.get('name')?.value,
          lastName: this.cardForm.get('lastname')?.value,
          addressLine: this.cardForm.get('addressLine')?.value,
          state: this.cardForm.get('state_name')?.value,
          city: this.cardForm.get('city')?.value,
          zipCode: this.cardForm.get('zipcode')?.value,
          paymentClientId: paramMap.get('paymentClient') ?? undefined,
          guestIdentifier: paramMap.get('guestIdentifier') ?? undefined,
          emailAddress: this.cardForm.get('email')?.value
        }

        if (isPlatformBrowser(this.platformId)) {
          const fingerPrint = this.cartService.getFingerPrintFromSession();
          if (fingerPrint) {
            paymentIntentionData['fingerprintTraceDto'] = fingerPrint ? {
              visitorId: fingerPrint ? fingerPrint.visitorId : null,
              requestId: fingerPrint ? fingerPrint.requestId : null
            } : null;
          } else {
            this.checkoutService.getFingerPrint().then((fingerPrint: any) => {
              paymentIntentionData['fingerprintTraceDto'] = fingerPrint ? {
                visitorId: fingerPrint ? fingerPrint.visitorId : null,
                requestId: fingerPrint ? fingerPrint.requestId : null
              } : null;
            });
          }
        }

        this.checkoutService.paymentIntentionGuest(paymentIntentionData).subscribe({
          next: async (response: any) => {
            if (!response.success) {
              this.isLoading = false;
              if (response?.responseCode === "PendingPaymentRequest") {
                this.dialog.open(NotificationModalComponent, {
                  disableClose: true,
                  width: 'auto',
                  data: {
                    msg: response.message,
                    btn_ok_text: 'CLOSE',
                    btn_cancel_txt: '',
                    redirect: '',
                    queryParams: ''
                  },
                }).afterClosed().subscribe(resp => {
                  const url = new URL(location.href);
                  url.pathname = 'payment-requests';
                  url.searchParams.delete('code');
                  url.searchParams.delete('total');
                  location.href = url.href;
                });
              } else {
                this.notificationService.showAndSubscribe(response.message, 'CLOSE');
              }
            } else {
              const paymentToken = response.data.paymentToken;
              this.authService.navigateToApp({
                redirect: '/cu/cart/checkout/start',
                paymentToken: paymentToken,
                paymentVariantId: 1
              }, paramMap.get('paymentRef') ?? undefined, true, paramMap.get('guestIdentifier') || undefined);
            }
          },
          error: (error) => {
            this.isLoading = false;
          }
        });
      }
    }
  }

  customValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      let value = null;
      if (control.value) {
        value = control.value.replace(/\s/g, '');
      }
      if (value && (value.length < 13 || value.length > 19 || !this.checkLuhn(value))) {
        return {'checkLuhnError': true};
      }
      return null;
    }
  }

  checkLuhn(cardNumber: string): boolean {
    let sum = 0;
    let isEven = false;
    for (let i = cardNumber.length - 1; i >= 0; i--) {
      let digit = Number(cardNumber[i]);
      if (isEven) {
        digit *= 2;
        if (digit > 9) {
          digit -= 9;
        }
      }
      sum += digit;
      isEven = !isEven;
    }
    return sum % 10 == 0;
  }

  keyupNumberCard(e: any) {
    const numberWithoutSpace = this.setNumberWithoutSpace(e.target.value);
    this.setCardType(numberWithoutSpace);
    const data = this.validateFormat(e.target.value);
    if (data.length && data.length % 5 == 0) {
      const chartIndex = data.length - 1;
      if (data.charAt(chartIndex) != ' ') {
        const chartData = data.charAt(chartIndex);
        const firstFours = data.substr(0, chartIndex);
        e.target.value = firstFours + ' ' + chartData;
      } else {
        e.target.value = data.substr(0, chartIndex);
      }
    } else {
      e.target.value = data;
    }
  }

  validateFormat(data: string): string {
    let result = data.split(' ').join('');
    if (result.length > 4) {
      const chopInto = Math.ceil(result.length / 4);
      const arraySubstr = [];
      for (let i = 0; i < chopInto; i++) {
        if (i == chopInto - 1) {
          arraySubstr.push(result.substr(i * 4));
        } else {
          arraySubstr.push(result.substr(i * 4, 4));
        }
      }
      let dataResult = '';
      arraySubstr.forEach((it, index) => {
        if (index == 0) {
          dataResult = it;
        } else {
          dataResult = dataResult + ' ' + it;
        }
      });
      return dataResult;
    }
    return result;
  }

  setNumberWithoutSpace(targetValue: string): string {
    let result = targetValue.split(' ').join('');
    return result;
  }

  setCardType(targetValue: string) {
    let num = targetValue[0];
    if (targetValue.length == 1) {
      this.verifyCard(num);
    } else {
      for (let k = 1; k < targetValue.length; k++) {
        num += targetValue[k];
        this.verifyCard(num);
      }
    }
  }

  verifyCard(digits: any): any {
    let rangeStart;
    let rangeEnd;
    let number;
    for (let index = 0; index < this.cardPredictors.length; index++) {
      for (let k = 0; k < this.cardPredictors[index].startWith.length; k++) {
        if (this.cardPredictors[index].startWith[k].includes('-')) {
          rangeStart = this.cardPredictors[index].startWith[k].split('-')[0];
          rangeEnd = this.cardPredictors[index].startWith[k].split('-')[1];
          if (digits >= rangeStart && digits <= rangeEnd) {
            return this.setData(index);
          }
        }
      }
      for (let j = 0; j < digits.length; j++) {
        if (j == 0) {
          number = digits[j];
        } else {
          number += digits[j];
        }
        if (this.cardPredictors[index].startWith.indexOf(number) !== -1) {
          return this.setData(index);
        }
      }
    }
  }

  setData(index: number) {
    const type = this.cardPredictors[index].type;
    // @ts-ignore
    const i = CARD_TYPES[`${type}`];
    const maxLength = Math.max(...this.cardPredictors[index].lenghts);
    this.largeMaxLength = maxLength % 4 == 0 ? maxLength + (Math.floor(maxLength / 4) - 1) : maxLength + Math.floor(maxLength / 4);
    this.cardForm.patchValue({
      cc_type: CARD_TYPES[i]
    });
    this.cardLogo = `../../../../assets/imgs/create-card/${CARD_TYPES[i]}.svg`;
  }

  allowNumbersAndSlash(e: any): boolean {
    let result = false;
    const charCode = e.charCode;
    if (charCode != 0) {
      if (charCode < 48 || charCode > 57) {
        e.preventDefault();
        result = false;
      } else {
        result = true;
      }
    }
    return result;
  }

  keyupExpirationCard(e: any) {
    const date = this.cardForm.get('expiration')?.value;
    const month = date?.split('/')[0];
    const year = date?.split('/')[1];
    const actualMonth = new Date().getUTCMonth() + 1;
    const actualYear = new Date().getUTCFullYear().toString().substring(2);

    this.wrongDate = (month > 12 || month < actualMonth && year <= actualYear) || year < actualYear;
    if (e?.key) {
      if (e.target.value.length == 2 && e.key !== 'Backspace') {
        e.target.value += '/';
      }
    } else if (e?.keyCode) {
      if (e.target.value.length == 2 && e.keyCode !== 8) {
        e.target.value += '/';
      }
    }
  }

  getAllCountries() {
    this.isLoadingAllCountries = true;
    this.addressService.getCountries().subscribe(countries => {
      this.allCountries = [...countries];
      if (this.billingCountriesToHide?.length === 0) {
        this.countries = [...countries];
      } else {
        this.countries = [...countries]
          .filter(country => this.billingCountriesToHide.some((countryCode) => country.iso !== countryCode));
      }
      this.isLoadingCountries = false;
      this.isLoadingAllCountries = false;
      this.isLoadingStates = false;
      this.isLoadingCities = false;
      this.showSelectInput = false;
      this.isLoadingPhones = false;
      this.isSkeleton = false;
    });
  }

  changeCountry(event: any) {
    this.isLoadingStates = true;
    this.showSelectInput = this.countryCu.iso == event.value;
    this.cardForm.patchValue({ state_name: null, city: null, zipcode: null, phone: null });
    const country = this.countries.find(it => it.iso === event.value);
    if (country) {
      this.countryIsoSelected = event.value.toLowerCase();
      this.changeViewPhone();
      this.addressService.getStates(country.id).subscribe((statesRequest) => {
        this.states = [...statesRequest];
        this.cities = [];
        this.postalCodes = [];
        this.isLoadingStates = false;
      });
    }
  }

  changeState(event: any) {
    this.isLoadingCities = true;
    this.cardForm.patchValue({ city: null, zipcode: null });
    const countryIso = this.cardForm.get('country_iso')?.value;
    const country = this.countries.find(it => it.iso === countryIso);
    if (country && this.countryCu.iso == countryIso) {
      this.addressService.getCities(countryIso, event.value).subscribe(citiesRequest => {
        this.cities = [...citiesRequest];
        this.postalCodes = [];
        this.isLoadingCities = false;
      });
    } else {
      this.isLoadingCities = false;
    }
  }

  changeCity(event: any) {
    this.cardForm.patchValue({ zipcode: null });
    const city = this.cities.find(it => it.name === event.value);
    if (city) {
      this.postalCodes = city.zip_code;
    } else {
      this.postalCodes = [];
    }
  }

  changePhone(event: any) {
    if (isPlatformBrowser(this.platformId)) {
      this.countryPrefix = event?.dialCode;
    }
  }

  changeViewPhone() {
    this.isLoadingPhones = true;
    setTimeout(() => {
      this.isLoadingPhones = false;
    }, 300);
  }

  setEnableForm() {
    Object.keys(this.cardForm.controls).forEach(key => {
      this.cardForm.get(key)?.enable();
    });
  }

  setDisableForm() {
    Object.keys(this.cardForm.controls).forEach(key => {
      this.cardForm.get(key)?.disable();
    });
  }

  get email() { return this.cardForm.get('email') as FormControl; }
  get name() { return this.cardForm.get('name') as FormControl; }
  get lastname() { return this.cardForm.get('lastname') as FormControl; }
  get number() { return this.cardForm.get('number') as FormControl; }
  get verification_value() { return this.cardForm.get('verification_value') as FormControl; }
  get expiration() { return this.cardForm.get('expiration') as FormControl; }
  get addressLine() { return this.cardForm.get('addressLine') as FormControl; }
  get country_iso() { return this.cardForm.get('country_iso') as FormControl; }
  get state_name() { return this.cardForm.get('state_name') as FormControl; }
  get city() { return this.cardForm.get('city') as FormControl; }
  get zipcode() { return this.cardForm.get('zipcode') as FormControl; }
}

